// ============================================================
// Data-Bound Widget Registry
// Static registry of all pre-built data-bound widgets.
// Each entry defines a widget that produces a pre-configured
// dataBound FormField when dragged onto the form canvas.
// ============================================================

import type { DataBoundWidgetDefinition, TemplateType } from '../types/templateTypes';

// ---------------------------------------------------------------------------
// TemplateType → EntityCategory Mapping
// ---------------------------------------------------------------------------

export const TEMPLATE_TYPE_TO_ENTITY_CATEGORIES: Record<TemplateType, Array<'parcel' | 'residential' | 'building' | 'hydrant' | 'site' | 'complex'>> = {
  wildland: ['parcel', 'residential', 'building', 'site'],
  fireCode: ['building'],
  hydrant: ['hydrant'],
  complex: ['complex', 'building'],
  communityRisk: ['building', 'residential', 'parcel', 'site'],
  custom: ['parcel', 'residential', 'building', 'hydrant', 'site', 'complex'],
};

// ---------------------------------------------------------------------------
// Widget Category Constants
// ---------------------------------------------------------------------------

export const WIDGET_CATEGORIES = {
  ENTITY_COMMON: 'Entity — Common',
  ENTITY_BUILDING: 'Entity — Building Details',
  ENTITY_HYDRANT: 'Entity — Hydrant Details',
  ENTITY_PARCEL: 'Entity — Parcel Details',
  ENTITY_SITE: 'Entity — Site Details',
  ENTITY_COMPLEX: 'Entity — Complex Details',
  ENTITY_SERVICE_AREA: 'Entity — Service Areas',
  EVENT_CORE: 'Event — Core',
  EVENT_BILLING: 'Event — Billing',
  CASE_INFO: 'Event — Case Info',
} as const;

// ---------------------------------------------------------------------------
// §3.1 — Common Entity Fields (All Entity Categories)
// ---------------------------------------------------------------------------

const COMMON_ENTITY_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'entity.name',
    label: 'Entity Name',
    dataSource: 'inspectableEntity',
    boundField: 'name',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'The name of the inspectable entity',
  },
  {
    widgetId: 'entity.description',
    label: 'Description',
    dataSource: 'inspectableEntity',
    boundField: 'description',
    displayFormat: 'text',
    inputType: 'textarea',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Entity description',
  },
  {
    widgetId: 'entity.entityCategory',
    label: 'Entity Category',
    dataSource: 'inspectableEntity',
    boundField: 'entityCategory',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'The category of the entity (parcel, building, hydrant, site, complex)',
  },
  {
    widgetId: 'entity.status',
    label: 'Entity Status',
    dataSource: 'inspectableEntity',
    boundField: 'status',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'entityStatus',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Current status of the entity',
  },
  {
    widgetId: 'entity.address.full',
    label: 'Full Address',
    dataSource: 'inspectableEntity',
    boundField: 'entityLocation.address.fullAddress',
    displayFormat: 'address',
    inputType: 'address',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Full formatted address',
  },
  {
    widgetId: 'entity.address.street',
    label: 'Street Address',
    dataSource: 'inspectableEntity',
    boundField: '',
    compositeFields: [
      'entityLocation.address.streetNumber',
      'entityLocation.address.predirectional',
      'entityLocation.address.streetName',
      'entityLocation.address.streetSuffix',
    ],
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    forceReadOnly: true,
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Composite street address (number + direction + name + suffix)',
  },
  {
    widgetId: 'entity.address.city',
    label: 'City',
    dataSource: 'inspectableEntity',
    boundField: 'entityLocation.address.city',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.address.state',
    label: 'State',
    dataSource: 'inspectableEntity',
    boundField: 'entityLocation.address.state',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.address.zip',
    label: 'ZIP Code',
    dataSource: 'inspectableEntity',
    boundField: 'entityLocation.address.zip',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.parcelNumber',
    label: 'Parcel Number',
    dataSource: 'inspectableEntity',
    boundField: 'parcelNumber',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.propertyType',
    label: 'Property Type',
    dataSource: 'inspectableEntity',
    boundField: 'propertyType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'propertyType',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.zoning',
    label: 'Zoning',
    dataSource: 'inspectableEntity',
    boundField: 'zoning',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'zoningCode',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.inspectionFrequency',
    label: 'Inspection Frequency',
    dataSource: 'inspectableEntity',
    boundField: 'inspectionFrequency',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'inspectionFrequency',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.lastInspectionDate',
    label: 'Last Inspection Date',
    dataSource: 'inspectableEntity',
    boundField: 'lastInspectionDate',
    displayFormat: 'date',
    inputType: 'date',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Date of the most recent inspection',
  },
  {
    widgetId: 'entity.nextScheduledInspection',
    label: 'Next Scheduled Inspection',
    dataSource: 'inspectableEntity',
    boundField: 'nextScheduledInspection',
    displayFormat: 'date',
    inputType: 'date',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
  },
  {
    widgetId: 'entity.tags',
    label: 'Tags',
    dataSource: 'inspectableEntity',
    boundField: 'tags',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Comma-separated tags',
  },
  {
    widgetId: 'entity.inspectionAccountName',
    label: 'Inspection Account',
    dataSource: 'inspectableEntity',
    boundField: 'inspectionAccountName',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Denormalized inspection account name',
  },
  {
    widgetId: 'entity.primaryContact.name',
    label: 'Primary Contact Name',
    dataSource: 'inspectableEntity',
    boundField: 'primaryContact.name',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Denormalized from contacts array',
  },
  {
    widgetId: 'entity.primaryContact.phone',
    label: 'Primary Contact Phone',
    dataSource: 'inspectableEntity',
    boundField: 'primaryContact.phone',
    displayFormat: 'phone',
    inputType: 'phone',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Denormalized from contacts array',
  },
  {
    widgetId: 'entity.primaryContact.email',
    label: 'Primary Contact Email',
    dataSource: 'inspectableEntity',
    boundField: 'primaryContact.email',
    displayFormat: 'email',
    inputType: 'email',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMMON,
    widgetCategoryOrder: 1,
    description: 'Denormalized from contacts array',
  },
];

// ---------------------------------------------------------------------------
// §3.2 — Building-Specific Fields (module: building)
// ---------------------------------------------------------------------------

const BUILDING_ENTITY_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'entity.building.buildingNumber',
    label: 'Building Number',
    dataSource: 'inspectableEntity',
    boundField: 'buildingDetails.buildingNumber',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['building'],
    requiredModule: 'building',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_BUILDING,
    widgetCategoryOrder: 2,
  },
  {
    widgetId: 'entity.building.yearBuilt',
    label: 'Year Built',
    dataSource: 'inspectableEntity',
    boundField: 'buildingDetails.yearBuilt',
    displayFormat: 'number',
    inputType: 'number',
    entityCategoryFilter: ['building'],
    requiredModule: 'building',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_BUILDING,
    widgetCategoryOrder: 2,
  },
  {
    widgetId: 'entity.building.squareFeet',
    label: 'Square Footage',
    dataSource: 'inspectableEntity',
    boundField: 'buildingDetails.squareFeet',
    displayFormat: 'number',
    inputType: 'number',
    entityCategoryFilter: ['building'],
    requiredModule: 'building',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_BUILDING,
    widgetCategoryOrder: 2,
  },
  {
    widgetId: 'entity.building.floors',
    label: 'Number of Floors',
    dataSource: 'inspectableEntity',
    boundField: 'buildingDetails.floors',
    displayFormat: 'number',
    inputType: 'number',
    entityCategoryFilter: ['building'],
    requiredModule: 'building',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_BUILDING,
    widgetCategoryOrder: 2,
  },
  {
    widgetId: 'entity.building.constructionType',
    label: 'Construction Type',
    dataSource: 'inspectableEntity',
    boundField: 'buildingDetails.constructionType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'constructionType',
    entityCategoryFilter: ['building'],
    requiredModule: 'building',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_BUILDING,
    widgetCategoryOrder: 2,
  },
  {
    widgetId: 'entity.building.sprinklered',
    label: 'Sprinklered',
    dataSource: 'inspectableEntity',
    boundField: 'buildingDetails.sprinklered',
    displayFormat: 'text',
    inputType: 'toggle',
    entityCategoryFilter: ['building'],
    requiredModule: 'building',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_BUILDING,
    widgetCategoryOrder: 2,
  },
  {
    widgetId: 'entity.building.buildingType',
    label: 'Building Type',
    dataSource: 'inspectableEntity',
    boundField: 'buildingDetails.buildingType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'buildingType',
    entityCategoryFilter: ['building'],
    requiredModule: 'building',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_BUILDING,
    widgetCategoryOrder: 2,
  },
];

// ---------------------------------------------------------------------------
// §3.3 — Hydrant-Specific Fields (module: hydrant)
// ---------------------------------------------------------------------------

const HYDRANT_ENTITY_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'entity.hydrant.hydrantNumber',
    label: 'Hydrant Number',
    dataSource: 'inspectableEntity',
    boundField: 'hydrantDetails.hydrantNumber',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['hydrant'],
    requiredModule: 'hydrant',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_HYDRANT,
    widgetCategoryOrder: 3,
  },
  {
    widgetId: 'entity.hydrant.hydrantType',
    label: 'Hydrant Type',
    dataSource: 'inspectableEntity',
    boundField: 'hydrantDetails.hydrantType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'hydrantType',
    entityCategoryFilter: ['hydrant'],
    requiredModule: 'hydrant',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_HYDRANT,
    widgetCategoryOrder: 3,
  },
  {
    widgetId: 'entity.hydrant.manufacturer',
    label: 'Manufacturer',
    dataSource: 'inspectableEntity',
    boundField: 'hydrantDetails.manufacturer',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['hydrant'],
    requiredModule: 'hydrant',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_HYDRANT,
    widgetCategoryOrder: 3,
  },
  {
    widgetId: 'entity.hydrant.installDate',
    label: 'Install Date',
    dataSource: 'inspectableEntity',
    boundField: 'hydrantDetails.installDate',
    displayFormat: 'date',
    inputType: 'date',
    entityCategoryFilter: ['hydrant'],
    requiredModule: 'hydrant',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_HYDRANT,
    widgetCategoryOrder: 3,
  },
  {
    widgetId: 'entity.hydrant.flowRate',
    label: 'Flow Rate (GPM)',
    dataSource: 'inspectableEntity',
    boundField: 'hydrantDetails.flowRate',
    displayFormat: 'number',
    inputType: 'number',
    entityCategoryFilter: ['hydrant'],
    requiredModule: 'hydrant',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_HYDRANT,
    widgetCategoryOrder: 3,
  },
  {
    widgetId: 'entity.hydrant.outlets',
    label: 'Number of Outlets',
    dataSource: 'inspectableEntity',
    boundField: 'hydrantDetails.outlets',
    displayFormat: 'number',
    inputType: 'number',
    entityCategoryFilter: ['hydrant'],
    requiredModule: 'hydrant',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_HYDRANT,
    widgetCategoryOrder: 3,
  },
];

// ---------------------------------------------------------------------------
// §3.4 — Parcel-Specific Fields (module: wildland)
// ---------------------------------------------------------------------------

const PARCEL_ENTITY_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'entity.parcel.book',
    label: 'Book',
    dataSource: 'inspectableEntity',
    boundField: 'parcelDetails.book',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['parcel'],
    requiredModule: 'wildland',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_PARCEL,
    widgetCategoryOrder: 4,
  },
  {
    widgetId: 'entity.parcel.page',
    label: 'Page',
    dataSource: 'inspectableEntity',
    boundField: 'parcelDetails.page',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['parcel'],
    requiredModule: 'wildland',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_PARCEL,
    widgetCategoryOrder: 4,
  },
  {
    widgetId: 'entity.parcel.block',
    label: 'Block',
    dataSource: 'inspectableEntity',
    boundField: 'parcelDetails.block',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['parcel'],
    requiredModule: 'wildland',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_PARCEL,
    widgetCategoryOrder: 4,
  },
  {
    widgetId: 'entity.parcel.parcel',
    label: 'Parcel',
    dataSource: 'inspectableEntity',
    boundField: 'parcelDetails.parcel',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['parcel'],
    requiredModule: 'wildland',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_PARCEL,
    widgetCategoryOrder: 4,
  },
  {
    widgetId: 'entity.parcel.acres',
    label: 'Acres',
    dataSource: 'inspectableEntity',
    boundField: 'parcelDetails.acres',
    displayFormat: 'number',
    inputType: 'number',
    entityCategoryFilter: ['parcel'],
    requiredModule: 'wildland',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_PARCEL,
    widgetCategoryOrder: 4,
  },
];

// ---------------------------------------------------------------------------
// §3.5 — Site-Specific Fields (module: site)
// ---------------------------------------------------------------------------

const SITE_ENTITY_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'entity.site.siteNumber',
    label: 'Site Number',
    dataSource: 'inspectableEntity',
    boundField: 'siteDetails.siteNumber',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['site'],
    requiredModule: 'site',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_SITE,
    widgetCategoryOrder: 5,
  },
  {
    widgetId: 'entity.site.siteType',
    label: 'Site Type',
    dataSource: 'inspectableEntity',
    boundField: 'siteDetails.siteType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'siteType',
    entityCategoryFilter: ['site'],
    requiredModule: 'site',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_SITE,
    widgetCategoryOrder: 5,
  },
];

// ---------------------------------------------------------------------------
// §3.6 — Complex-Specific Fields (module: complex)
// ---------------------------------------------------------------------------

const COMPLEX_ENTITY_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'entity.complex.complexName',
    label: 'Complex Name',
    dataSource: 'inspectableEntity',
    boundField: 'complexDetails.complexName',
    displayFormat: 'text',
    inputType: 'text',
    entityCategoryFilter: ['complex'],
    requiredModule: 'complex',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMPLEX,
    widgetCategoryOrder: 6,
  },
  {
    widgetId: 'entity.complex.complexType',
    label: 'Complex Type',
    dataSource: 'inspectableEntity',
    boundField: 'complexDetails.complexType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'complexType',
    entityCategoryFilter: ['complex'],
    requiredModule: 'complex',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_COMPLEX,
    widgetCategoryOrder: 6,
  },
];

// ---------------------------------------------------------------------------
// §3.7 — Service Area Widget (dynamic, single widget)
// ---------------------------------------------------------------------------

const SERVICE_AREA_ENTITY_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'entity.serviceArea',
    label: 'Service Area',
    dataSource: 'inspectableEntity',
    boundField: '',
    displayFormat: 'lookup',
    inputType: 'lookup',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.ENTITY_SERVICE_AREA,
    widgetCategoryOrder: 7,
    description: 'Dynamic widget — select a service area type in the Properties panel after adding to the form',
  },
];

// ---------------------------------------------------------------------------
// §4.1 — Event Core Fields
// ---------------------------------------------------------------------------

const EVENT_CORE_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'event.eventType',
    label: 'Event Type',
    dataSource: 'inspectionEvent',
    boundField: 'eventType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'eventType',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
    description: 'Set at creation, cannot be changed',
  },
  {
    widgetId: 'event.inspectionType',
    label: 'Inspection Type',
    dataSource: 'inspectionEvent',
    boundField: 'inspectionType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'inspectionType',
    defaultMode: 'editable',
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
    description: 'Granular inspection visit classification (residential, assembly, AB38, defensible space, etc.)',
  },
  {
    widgetId: 'event.status',
    label: 'Event Status',
    dataSource: 'inspectionEvent',
    boundField: 'status',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    forceReadOnly: true,
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
    description: 'Managed by workflow engine',
  },
  {
    widgetId: 'event.reinspectionNumber',
    label: 'Reinspection #',
    dataSource: 'inspectionEvent',
    boundField: 'reinspectionNumber',
    displayFormat: 'number',
    inputType: 'number',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
  },
  {
    widgetId: 'event.primaryInspectorName',
    label: 'Primary Inspector',
    dataSource: 'inspectionEvent',
    boundField: 'primaryInspectorName',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
  },
  {
    widgetId: 'event.scheduledDate',
    label: 'Scheduled Date',
    dataSource: 'inspectionEvent',
    boundField: 'scheduledDate',
    displayFormat: 'date',
    inputType: 'date',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
  },
  {
    widgetId: 'event.startedAt',
    label: 'Started At',
    dataSource: 'inspectionEvent',
    boundField: 'startedAt',
    displayFormat: 'date',
    inputType: 'date',
    defaultMode: 'readOnly',
    forceReadOnly: true,
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
    description: 'Auto-set when inspection begins',
  },
  {
    widgetId: 'event.completedAt',
    label: 'Completed At',
    dataSource: 'inspectionEvent',
    boundField: 'completedAt',
    displayFormat: 'date',
    inputType: 'date',
    defaultMode: 'readOnly',
    forceReadOnly: true,
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
    description: 'Auto-set when inspection is completed',
  },
  {
    widgetId: 'event.internalNotes',
    label: 'Internal Notes',
    dataSource: 'inspectionEvent',
    boundField: 'internalNotes',
    displayFormat: 'text',
    inputType: 'textarea',
    defaultMode: 'editable',
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
  },
  {
    widgetId: 'event.externalNotes',
    label: 'External Notes',
    dataSource: 'inspectionEvent',
    boundField: 'externalNotes',
    displayFormat: 'text',
    inputType: 'textarea',
    defaultMode: 'editable',
    widgetCategory: WIDGET_CATEGORIES.EVENT_CORE,
    widgetCategoryOrder: 8,
  },
];

// ---------------------------------------------------------------------------
// §4.2 — Event Billing Fields (module: billing)
// ---------------------------------------------------------------------------

const EVENT_BILLING_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'event.billing.inspectionFee',
    label: 'Inspection Fee',
    dataSource: 'inspectionEvent',
    boundField: 'billing.inspectionFee',
    displayFormat: 'currency',
    inputType: 'number',
    requiredModule: 'billing',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_BILLING,
    widgetCategoryOrder: 9,
  },
  {
    widgetId: 'event.billing.reinspectionFee',
    label: 'Reinspection Fee',
    dataSource: 'inspectionEvent',
    boundField: 'billing.reinspectionFee',
    displayFormat: 'currency',
    inputType: 'number',
    requiredModule: 'billing',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_BILLING,
    widgetCategoryOrder: 9,
  },
  {
    widgetId: 'event.billing.travelTime',
    label: 'Travel Time (min)',
    dataSource: 'inspectionEvent',
    boundField: 'billing.travelTime',
    displayFormat: 'number',
    inputType: 'number',
    requiredModule: 'billing',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_BILLING,
    widgetCategoryOrder: 9,
  },
  {
    widgetId: 'event.billing.calculatedFee',
    label: 'Calculated Fee',
    dataSource: 'inspectionEvent',
    boundField: 'billing.calculatedFee',
    displayFormat: 'currency',
    inputType: 'number',
    requiredModule: 'billing',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_BILLING,
    widgetCategoryOrder: 9,
  },
  {
    widgetId: 'event.billing.feeWaived',
    label: 'Fee Waived',
    dataSource: 'inspectionEvent',
    boundField: 'billing.feeWaived',
    displayFormat: 'text',
    inputType: 'toggle',
    requiredModule: 'billing',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_BILLING,
    widgetCategoryOrder: 9,
  },
  {
    widgetId: 'event.billing.feeWaivedReason',
    label: 'Fee Waived Reason',
    dataSource: 'inspectionEvent',
    boundField: 'billing.feeWaivedReason',
    displayFormat: 'text',
    inputType: 'textarea',
    requiredModule: 'billing',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.EVENT_BILLING,
    widgetCategoryOrder: 9,
  },
];

// ---------------------------------------------------------------------------
// §4.3 — Case-Level Fields (dataSource: cases)
// Note: Widget IDs use "case." prefix per D17. dataSource updated
// to 'cases' following the collection rename.
// ---------------------------------------------------------------------------

const CASE_LEVEL_WIDGETS: DataBoundWidgetDefinition[] = [
  {
    widgetId: 'case.caseNumber',
    label: 'Case Number',
    dataSource: 'cases',
    boundField: 'caseNumber',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    forceReadOnly: true,
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
    description: 'Auto-generated case identifier. Requires caseNumber field to be added to InspectionCase schema.',
  },
  {
    widgetId: 'case.caseType',
    label: 'Case Type',
    dataSource: 'cases',
    boundField: 'caseType',
    displayFormat: 'lookup',
    inputType: 'lookup',
    lookupType: 'caseType',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
  {
    widgetId: 'case.fiscalYear',
    label: 'Fiscal Year',
    dataSource: 'cases',
    boundField: 'fiscalYear',
    displayFormat: 'number',
    inputType: 'number',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
  {
    widgetId: 'case.caseStatus',
    label: 'Case Status',
    dataSource: 'cases',
    boundField: 'caseStatus',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    forceReadOnly: true,
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
  {
    widgetId: 'case.templateName',
    label: 'Template Name',
    dataSource: 'cases',
    boundField: 'templateName',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
  {
    widgetId: 'case.entityName',
    label: 'Entity Name',
    dataSource: 'cases',
    boundField: 'entityName',
    displayFormat: 'text',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
  {
    widgetId: 'case.entityAddress',
    label: 'Entity Address',
    dataSource: 'cases',
    boundField: 'entityAddress',
    displayFormat: 'address',
    inputType: 'text',
    defaultMode: 'readOnly',
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
  {
    widgetId: 'case.internalNotes',
    label: 'Case Internal Notes',
    dataSource: 'cases',
    boundField: 'internalNotes',
    displayFormat: 'text',
    inputType: 'textarea',
    defaultMode: 'editable',
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
  {
    widgetId: 'case.externalNotes',
    label: 'Case External Notes',
    dataSource: 'cases',
    boundField: 'externalNotes',
    displayFormat: 'text',
    inputType: 'textarea',
    defaultMode: 'editable',
    widgetCategory: WIDGET_CATEGORIES.CASE_INFO,
    widgetCategoryOrder: 10,
  },
];

// ---------------------------------------------------------------------------
// Combined Registry
// ---------------------------------------------------------------------------

export const DATA_BOUND_WIDGET_REGISTRY: DataBoundWidgetDefinition[] = [
  ...COMMON_ENTITY_WIDGETS,
  ...BUILDING_ENTITY_WIDGETS,
  ...HYDRANT_ENTITY_WIDGETS,
  ...PARCEL_ENTITY_WIDGETS,
  ...SITE_ENTITY_WIDGETS,
  ...COMPLEX_ENTITY_WIDGETS,
  ...SERVICE_AREA_ENTITY_WIDGETS,
  ...EVENT_CORE_WIDGETS,
  ...EVENT_BILLING_WIDGETS,
  ...CASE_LEVEL_WIDGETS,
];

// ---------------------------------------------------------------------------
// Lookup Helpers
// ---------------------------------------------------------------------------

/** Get a widget definition by its widgetId */
export function getWidgetById(widgetId: string): DataBoundWidgetDefinition | undefined {
  return DATA_BOUND_WIDGET_REGISTRY.find(w => w.widgetId === widgetId);
}

/** Get all widgets for a given category label */
export function getWidgetsByCategory(category: string): DataBoundWidgetDefinition[] {
  return DATA_BOUND_WIDGET_REGISTRY.filter(w => w.widgetCategory === category);
}

/** Get all unique widget categories in display order */
export function getWidgetCategories(): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const w of DATA_BOUND_WIDGET_REGISTRY) {
    if (!seen.has(w.widgetCategory)) {
      seen.add(w.widgetCategory);
      result.push(w.widgetCategory);
    }
  }
  return result;
}

/**
 * Get entity widget categories that should be shown for a given template type,
 * filtered by enabled modules.
 */
export function getVisibleEntityCategories(
  templateType: TemplateType,
  hasModule: (key: string) => boolean,
): string[] {
  const applicableEntityCategories = TEMPLATE_TYPE_TO_ENTITY_CATEGORIES[templateType] || [];

  return getWidgetCategories().filter(cat => {
    // Event/Case categories are always shown
    if (!cat.startsWith('Entity')) return false;

    // Common and Service Areas are always shown
    if (cat === WIDGET_CATEGORIES.ENTITY_COMMON || cat === WIDGET_CATEGORIES.ENTITY_SERVICE_AREA) {
      return true;
    }

    // Check if any widget in this category passes both filters
    const widgets = getWidgetsByCategory(cat);
    return widgets.some(w => {
      // Module gating
      if (w.requiredModule && !hasModule(w.requiredModule)) return false;
      // Entity category filtering
      if (w.entityCategoryFilter) {
        return w.entityCategoryFilter.some(ec => applicableEntityCategories.includes(ec));
      }
      return true;
    });
  });
}

/**
 * Get event/case widget categories that should be shown,
 * filtered by enabled modules.
 */
export function getVisibleEventCategories(
  hasModule: (key: string) => boolean,
): string[] {
  return getWidgetCategories().filter(cat => {
    if (cat.startsWith('Entity')) return false;

    const widgets = getWidgetsByCategory(cat);
    return widgets.some(w => {
      if (w.requiredModule && !hasModule(w.requiredModule)) return false;
      return true;
    });
  });
}

/**
 * Get all widgets visible for a given template type and module configuration.
 */
export function getVisibleWidgets(
  templateType: TemplateType,
  hasModule: (key: string) => boolean,
): DataBoundWidgetDefinition[] {
  const applicableEntityCategories = TEMPLATE_TYPE_TO_ENTITY_CATEGORIES[templateType] || [];

  return DATA_BOUND_WIDGET_REGISTRY.filter(w => {
    // Module gating
    if (w.requiredModule && !hasModule(w.requiredModule)) return false;

    // Entity category filtering (only for entity widgets)
    if (w.dataSource === 'inspectableEntity' && w.entityCategoryFilter) {
      if (!w.entityCategoryFilter.some(ec => applicableEntityCategories.includes(ec))) {
        return false;
      }
    }

    return true;
  });
}
